import { faCaretDown } from '@fortawesome/pro-solid-svg-icons/faCaretDown';
import memoize from 'fast-memoize';
import { connect } from 'formik';
import React, { Component, PropsWithChildren } from 'react';
import { createPortal } from 'react-dom';
import posed from 'react-pose';
import { Collapse, RegisterElement } from '..';
import { ExternalClickWrapper } from '../ExternalClickWrapper/ExternalClickWrapper';
import { FormGroupContext } from '../FormGroup/FormGroup';
import { Icon } from '../Icon';
import { resolveProps } from '../utils/formikUtils';
import { createId } from '../utils/IdGenerator';
import { NaturalFieldOption } from './NaturalFieldOption';
import './NaturalFieldSelect.scss';
import {
  INaturalFieldOption,
  INaturalFieldOptionsGroup,
  INaturalFieldProps,
  InnerNaturalFieldOption,
  InnerNaturalFieldOptionsGroup,
  InnerNaturalFieldProps,
} from './types';
import Scrollbars from 'react-custom-scrollbars';

// keyCodes
const CODE = {
  DOWN: 40,
  ENTER: 13,
  ESC: 27,
  SPACE: 32,
  TAB: 9,
  UP: 38,
};

const Overlay = posed.div({
  closed: {
    applyAtEnd: { display: 'none' },
    opacity: 0,
    transition: {
      duration: 200,
    },
  },
  open: {
    applyAtStart: { display: 'block' },
    opacity: 0.2,
    transition: {
      duration: 200,
    },
  },
});

export interface INaturalFieldState {
  expanded: boolean;
  expandedOptionsGroups?: string[];
  highlightedOptionIndex: number | null;
}

export class NaturalFieldSelect extends Component<PropsWithChildren<InnerNaturalFieldProps>, INaturalFieldState> {
  context: any;
  private get selections() {
    const m = new Map();
    const values = this.getValue();
    if (values) {
      values.forEach((value: any) => m.set(value, value));
    }
    return m;
  }
  /**
   * Augment (and cache) the options we received with unique IDs and index numbers for keyboard navigation
   */
  public options = memoize(
    (
      options: (INaturalFieldOptionsGroup | INaturalFieldOption)[],
      // Even though the param below is not used inside the function directly,
      // it’s one of the two cache keys for memoize, and is necessary to make sure
      // the function gets refreshed if its contents change.
      expandedOptionsGroups = this.state.expandedOptionsGroups,
    ) => {
      let index = 0;

      const innerOptions: (InnerNaturalFieldOptionsGroup | InnerNaturalFieldOption)[] = options.map((option) => {
        if (isOptionsGroup(option)) {
          const optionsGroup = option;
          return {
            ...option,
            index: index++,
            options: optionsGroup.options.map((subOption): InnerNaturalFieldOption => {
              return {
                ...subOption,
                id: createId('nf-option'),
                index: this.isExpanded(optionsGroup) ? index++ : -1,
              };
            }),
          };
        } else {
          return {
            id: createId('nf-option'),
            index: index++,
            ...option,
          };
        }
      });

      return innerOptions;
    },
  );

  public readonly state: Readonly<INaturalFieldState> = {
    expanded: false,
    highlightedOptionIndex: null,
  };

  private id = createId('natural-field');
  private linkEl: HTMLAnchorElement | null;

  public componentDidMount() {
    this.props.formik.registerField && this.props.formik.registerField(this.props.name, this);
    window.document.addEventListener('keydown', this.onEscape, true);

    this.expandOptionsGroupsFromProps();
  }

  public componentDidUpdate(prevProps: INaturalFieldProps) {
    if (prevProps.options !== this.props.options) {
      this.expandOptionsGroupsFromProps();
    }
    const { touched, valid } = resolveProps(this.props, this.props.formik);
    const showError = touched && !valid;
    if (this.context !== null && showError !== this.context.formGroupErrors.get(this.props.name)) {
      this.context.updateFormGroupErrors(this.props.name, showError);
    }
  }

  public componentWillUnmount() {
    this.props.formik.unregisterField && this.props.formik.unregisterField(this.props.name);
    window.document.removeEventListener('keydown', this.onEscape, true);
  }

  public handleOverlayClick = () => {
    this.setState(() => ({ expanded: false }));
    this.linkEl!.focus();
  };

  public handleOptionClick = (option: string) => {
    const { multiple = false, name, onChange, formik } = this.props;
    const isSingle = !multiple;
    const S = this.selections; // this is a Map
    if (Object.keys(formik).length && (!formik.touched || !formik.touched[name])) {
      formik.setFieldTouched(name, true);
    }

    if (isSingle) {
      if (S.size) {
        // unselect anything that is selected
        Array.from(S.keys()).forEach((v) => S.delete(v));
      }
      S.set(option, option);
    }

    if (multiple && S.has(option)) {
      S.delete(option);
    } else if (multiple) {
      S.set(option, option);
    }

    const newValues = Array.from(S.values());

    if (onChange) {
      onChange(name, newValues);
    } else if (Object.keys(formik).length && name && formik.values[name] !== undefined) {
      formik.setFieldValue(name, newValues);
    }

    if (!multiple) {
      this.focus();
      this.toggleExpand(false);
    }
  };

  public toggleExpand = (force?: boolean) => {
    this.setState((prevState: INaturalFieldState) => {
      const expanded = force === undefined ? !prevState.expanded : force;
      return {
        expanded,
        highlightedOptionIndex: null,
      };
    });
  };

  public getSelectedOptions = () => {
    const options = this.options(this.props.options, this.state.expandedOptionsGroups);
    const selections = this.selections;
    const matches: InnerNaturalFieldOption[] = [];
    const findOptions = (values: Map<any, any>, opts: (InnerNaturalFieldOptionsGroup | InnerNaturalFieldOption)[]) => {
      opts.forEach((option) => {
        if (isOptionsGroup(option)) {
          findOptions(values, option.options);
        } else {
          if (values.has(option.value)) {
            matches.push(option);
          }
        }
      });
    };

    findOptions(selections, options);
    return matches;
  };

  /**
   * Get the number of visible options (we don't want to include items in collapsed options groups)
   */
  public getOptionsLength = (
    options: (
      | INaturalFieldOptionsGroup
      | InnerNaturalFieldOptionsGroup
      | INaturalFieldOption
      | InnerNaturalFieldOption
    )[],
  ) => {
    let length = 0;
    options.forEach((option) => {
      if (isOptionsGroup(option)) {
        length += 1;
        if (this.isExpanded(option)) {
          length += option.options.length;
        }
      } else {
        length += 1;
      }
    });

    return length;
  };

  public formatSelected = () => {
    const { placeholder = 'select ...' } = this.props;
    const selections = this.selections;
    const selectedOptions = this.getSelectedOptions();

    if (selections.size === 0) {
      return <span>{placeholder}</span>;
    }

    if (this.props.formatSelected) {
      return this.props.formatSelected(selectedOptions);
    }

    if (this.props.multiple) {
      const formattedOptions = selectedOptions.map((option) => option.display || option.value).join(', ');

      return formattedOptions;
    }

    if (selectedOptions.length === 0) {
      return [];
    } else {
      return selectedOptions[0].display || selectedOptions[0].value;
    }
  };

  public renderList(labeledById: string) {
    const { options: initialOptions, width } = resolveProps(this.props, this.props.formik);

    const options = this.options(initialOptions, this.state.expandedOptionsGroups);

    const { top, left, bottom, height } = this.getLinkPosition();

    const activeIndex = this.state.highlightedOptionIndex;
    let activeOptionId;
    if (typeof activeIndex === 'number') {
      const activeOption = this.getOptionByIndex(activeIndex, options);
      if (isOption(activeOption)) {
        activeOptionId = activeOption.id;
      }
    }

    const style = this.props.openAbove
      ? { left: left + window.scrollX, bottom: window.innerHeight - bottom + window.scrollY + height, width }
      : {
          left: left + window.scrollX,
          top: top + window.scrollY + height,
          width,
        };
    const list = (
      <ExternalClickWrapper onExternal={this.handleOverlayClick}>
        <div
          className={'nl-popover'}
          data-testid={'nl-popover'}
          style={style}
          aria-labelledby={labeledById}
          onKeyDown={this.keyboardListener}
          tabIndex={0} // so keyboard listener is in the context of this node
          ref={(e) => {
            if (e) {
              e.focus();
            }
          }}
          aria-activedescendant={activeOptionId}
          role={'listbox'}
        >
          <Scrollbars autoHeight={true} autoHeightMax={'156px'}>
            {options.map((option) =>
              isOptionsGroup(option) ? this.renderOptionsGroup(option) : this.renderOption(option),
            )}
          </Scrollbars>
        </div>
      </ExternalClickWrapper>
    );

    return createPortal(list, document.body);
  }

  public renderOptionsGroup(optionsGroup: InnerNaturalFieldOptionsGroup) {
    const { highlightedOptionIndex: activeIndex } = this.state;
    const expanded = this.isExpanded(optionsGroup);

    return (
      <div className="nl-select-options-group" key={optionsGroup.display}>
        <p
          className={`nl-select-options-group-header ${activeIndex === optionsGroup.index && 'highlighted'}`}
          onClick={() => this.toggleOptionsGroupExpand(optionsGroup)}
        >
          <Icon icon={faCaretDown} size="sm" className={`nl-expand-icon ${expanded ? 'expanded' : 'collapsed'}`} />
          {optionsGroup.display}
        </p>
        <Collapse isOpen={expanded}>
          <div>{optionsGroup.options.map((option: InnerNaturalFieldOption) => this.renderOption(option))}</div>
        </Collapse>
      </div>
    );
  }

  public renderOption(option: InnerNaturalFieldOption) {
    const { multiple = false } = resolveProps(this.props, this.props.formik);
    const activeIndex = this.state.highlightedOptionIndex;

    return (
      <NaturalFieldOption
        id={option.id}
        key={option.id}
        highlighted={activeIndex === option.index}
        display={option.display}
        value={option.value}
        selected={this.selections.has(option.value)}
        onClick={this.handleOptionClick}
        multiple={multiple}
        disabled={option.disabled}
      />
    );
  }

  public render() {
    const {
      color = '#444',
      id = this.id,
      title = 'Click to see available options',
      errorMessage,
      valid = true,
      touched = false,
      name,
    } = resolveProps(this.props, this.props.formik);
    const showError = touched && !valid;

    const showSuccess = touched && valid;

    const errorBorder = showError && errorMessage ? { border: '2px dashed #f44123', padding: '0 3px' } : {};
    const linkStyles = {
      borderBottomColor: color,
      color,
      ...errorBorder,
    };

    return (
      <>
        <RegisterElement name={`register-${id}`}>
          {(ref: any) => {
            const myRef = showError ? ref : undefined;
            return (
              <div ref={myRef} id={`${id}-wrapper`} className={'nl-container'} qa-id={this.props['qa-id']}>
                <a
                  id={id}
                  className="nl-field-link"
                  title={showError ? errorMessage : title}
                  style={linkStyles}
                  onClick={(e) => {
                    this.toggleExpand();
                    e.stopPropagation();
                  }}
                  onKeyDown={(e) => {
                    if (e.keyCode === CODE.ENTER || e.keyCode === CODE.SPACE) {
                      this.toggleExpand();
                      e.preventDefault();
                      e.stopPropagation();
                    }
                  }}
                  ref={(ref2) => (this.linkEl = ref2)}
                  aria-haspopup={true}
                  aria-expanded={this.state.expanded}
                  tabIndex={0}
                  onBlur={
                    () => this.handleBlur()
                    // context.displayChildError(showError, parentErrorMessage)
                  }
                >
                  {this.formatSelected()}
                </a>
                {this.props.showArrow && (
                  <a
                    role="button"
                    onClick={(e) => {
                      this.toggleExpand();
                      e.stopPropagation();
                    }}
                    title="Click to see available options"
                    className="nl-caret-down"
                  >
                    <Icon icon={faCaretDown} size="sm" />
                  </a>
                )}
                {this.state.expanded && this.renderList(id)}
              </div>
            );
          }}
        </RegisterElement>
        <Overlay className="nl-overlay" pose={this.state.expanded ? 'open' : 'closed'} />
        <span className="sr-only" id={`${id}Status`}>
          {showError && '(error)'}
          {showSuccess && '(success)'}
        </span>
      </>
    );
  }

  private handleBlur = () => {
    const { name, formik } = this.props;
    if (this.props.formik && this.props.formik.handleBlur) {
      this.props.formik.handleBlur(name);
    }
    if (Object.keys(formik).length && (!formik.touched || !formik.touched[name])) {
      formik.setFieldTouched(name, true);
    }
  };

  private getOptionByIndex = (
    index: number,
    arr: (InnerNaturalFieldOptionsGroup | InnerNaturalFieldOption)[],
  ): InnerNaturalFieldOptionsGroup | InnerNaturalFieldOption | undefined => {
    let match;
    for (let i = 0; i < arr.length && !match; i += 1) {
      const option = arr[i];
      if (option.index === index) {
        match = option;
      } else if (isOptionsGroup(option)) {
        match = this.getOptionByIndex(index, option.options);
      }
    }

    return match;
  };

  private focus() {
    this.linkEl && this.linkEl.focus();
  }

  private getLinkPosition() {
    if (this.linkEl) {
      return this.linkEl.getBoundingClientRect();
    }
    return {
      bottom: 0,
      height: 0,
      left: 0,
      right: 0,
      top: 0,
      width: 0,
      x: 0,
      y: 0,
    };
  }

  private getValue(): string[] {
    const { value = [], multiple } = resolveProps(this.props, this.props.formik);
    if (value.length === 0) {
      return [];
    } else {
      // make sure that we are only returning one value if in single-select mode
      return multiple ? value : value.slice(0, 1);
    }
  }

  private toggleOptionsGroupExpand = (optionsGroup: InnerNaturalFieldOptionsGroup) => {
    this.setState((prevState) => {
      const expanded = prevState.expandedOptionsGroups || [];

      if (expanded.includes(optionsGroup.display)) {
        return {
          expandedOptionsGroups: expanded.filter((display) => display !== optionsGroup.display),
        };
      } else {
        return {
          expandedOptionsGroups: expanded.concat(optionsGroup.display),
        };
      }
    });
  };

  private handleUpDown(direction: number) {
    this.setState((prevState) => {
      const options = this.options(this.props.options, this.state.expandedOptionsGroups);

      let index = prevState.highlightedOptionIndex;
      index = index === null ? 0 : index + direction;

      const optionsLength = this.getOptionsLength(options);

      if (index >= optionsLength) {
        index = 0;
      } else if (index < 0) {
        index = optionsLength - 1;
      }

      return { highlightedOptionIndex: index };
    });
  }

  // listening for keyboard clicks in the popover options list
  private keyboardListener = (e: any) => {
    const { keyCode: key } = e;
    const { DOWN, ENTER, ESC, SPACE, TAB, UP } = CODE;
    if (key === TAB || key === ESC) {
      this.focus();
      this.toggleExpand();
    } else if (key === ENTER || key === SPACE) {
      // same as clicking on an option
      const { highlightedOptionIndex: index } = this.state;
      if (index !== null) {
        const options = this.options(this.props.options, this.state.expandedOptionsGroups);
        const selectedOption = this.getOptionByIndex(index, options);

        if (isOptionsGroup(selectedOption)) {
          this.toggleOptionsGroupExpand(selectedOption);
        } else if (isOption(selectedOption)) {
          this.handleOptionClick(selectedOption.value);
        }
      }
    } else if (key === DOWN) {
      this.handleUpDown(1);
    } else if (key === UP) {
      this.handleUpDown(-1);
    }

    // if key is something we were listening for then suppress the event
    const suppress = [DOWN, ENTER, SPACE, TAB, UP].indexOf(key) !== -1;
    if (suppress) {
      e.preventDefault();
      e.stopPropagation();
    }
  };

  private onEscape = (e: KeyboardEvent) => {
    if (this.state.expanded && e.keyCode === 27) {
      this.toggleExpand();
      e.stopPropagation();
    }
  };

  private isExpanded = (optionsGroup: INaturalFieldOptionsGroup | InnerNaturalFieldOptionsGroup) => {
    const { expandedOptionsGroups = [] } = this.state;

    return expandedOptionsGroups.includes(optionsGroup.display);
  };

  private expandOptionsGroupsFromProps() {
    const { expandedOptionsGroups = [] } = this.state;
    const optionsGroupsToAdd: string[] = [];
    this.props.options.forEach((option) => {
      if (isOptionsGroup(option)) {
        if (option.expanded && !expandedOptionsGroups.includes(option.display)) {
          optionsGroupsToAdd.push(option.display);
        }
      }
    });
    this.setState(() => {
      return {
        expandedOptionsGroups: expandedOptionsGroups.concat(optionsGroupsToAdd),
      };
    });
  }
}

NaturalFieldSelect.contextType = FormGroupContext;

function isOption(option: INaturalFieldOptionsGroup | INaturalFieldOption | undefined): option is INaturalFieldOption {
  return !!option && option.hasOwnProperty('value');
}

function isOptionsGroup(
  option: INaturalFieldOptionsGroup | INaturalFieldOption | undefined,
): option is INaturalFieldOptionsGroup {
  return !!option && option.hasOwnProperty('options');
}

export default connect<INaturalFieldProps>(NaturalFieldSelect);
